// TelcoTrack — communication MIME → Drive + documents (not filing-slot přílohy).
//
// Přílohy (attachments / outbound first-request slots):
//   Outbound first-filing package on the stavby Drive (C1/C2/TZ / R100…), keyed by
//   `packageKey` via `proposedFilingDriveArtifactKey`. Not message MIME bytes.
//
// Dokumenty (documents / inbound response evidence):
//   Inbound reply files on a classified communication — create `documents` rows and
//   auto-file onto the app-connected Drive under a skeleton leaf from the
//   communication topic (téma: documentType / subjectKey / agendaKind).

import {
  isAuthorityPovinnostKey,
  isAuthorityPovinnostLegacyKey,
} from './authority-domain.constants';
import {
  discoveryEntry,
  isAuthorityPropertyTrackType,
  NETWORK_STAKEOUT_DOCUMENT_TYPE,
  uradyCategoryForObligation,
} from './authority-urady-categories.constants';
import {
  PROJECT_DRIVE_POSTA_KEY,
  projectDriveSkeletonEntry,
  type ProjectDriveArtifactFolderKey,
} from './project-drive-folder.constants';

export const AUTHORITY_MESSAGE_DRIVE_SKIP_REASONS = [
  'unassigned',
  'no_bytes',
  'already_synced',
  'recommended_docs',
] as const;

export type AuthorityMessageDriveSkipReason = (typeof AUTHORITY_MESSAGE_DRIVE_SKIP_REASONS)[number];

export type AuthorityMessageDrivePersistDecision =
  | { action: 'skip'; reason: AuthorityMessageDriveSkipReason }
  | { action: 'persist' };

/**
 * Whether to upload a stored MIME attachment of a classified communication to the stavby Drive.
 * Filing-package / recommended-docs paths are always skipped — those are přílohy (first-request
 * Drive slots), not dokumenty created from message bytes.
 */
export function decideAuthorityMessageAttachmentDrivePersist(input: {
  polygonId?: string | null;
  storageKey?: string | null;
  driveFileId?: string | null;
  /** Must stay empty — communication documents are not C1/C2/TZ slots. */
  packageKey?: string | null;
  /** Explicit recommended-docs / proposed-file catalog path. */
  useRecommendedDocs?: boolean;
}): AuthorityMessageDrivePersistDecision {
  if (input.useRecommendedDocs === true) {
    return { action: 'skip', reason: 'recommended_docs' };
  }
  if ((input.packageKey ?? '').trim()) {
    return { action: 'skip', reason: 'recommended_docs' };
  }
  if (!(input.polygonId ?? '').trim()) {
    return { action: 'skip', reason: 'unassigned' };
  }
  if (!(input.storageKey ?? '').trim()) {
    return { action: 'skip', reason: 'no_bytes' };
  }
  if ((input.driveFileId ?? '').trim()) {
    return { action: 'skip', reason: 'already_synced' };
  }
  return { action: 'persist' };
}

/**
 * Topic (téma komunikace) → canonical skeleton leaf under `02_POVOLENÍ_A_AKVIZICE`.
 * Unknown / untyped communications fall back to `posta` (`08_Komunikace`).
 */
export function authorityCommunicationTopicDriveFolderKey(input: {
  documentType?: string | null;
  subjectKey?: string | null;
  agendaKind?: string | null;
}): ProjectDriveArtifactFolderKey {
  const documentType = (input.documentType ?? '').trim().toLowerCase();
  const subjectKey = (input.subjectKey ?? '').trim().toLowerCase();
  const agendaKind = (input.agendaKind ?? '').trim().toLowerCase();
  const key = documentType || subjectKey;

  if (key === 'dio' || subjectKey === 'dio') return 'dio';
  if (
    key === 'vecne_bremeno' ||
    key === 'evbr' ||
    subjectKey === 'evbr' ||
    subjectKey === 'vecne_bremeno'
  ) {
    return 'evbr';
  }
  if (key === 'souhlas_vlastnika' || key === 'vypis_kn' || isAuthorityPropertyTrackType(key)) {
    if (key === 'vecne_bremeno') return 'evbr';
    return 'acquisition';
  }
  if (key === 'digitalni_cesta' || key === 'digital_path' || key === 'digitalni-cesta') {
    return 'digitalPath';
  }
  if (
    key === 'stakeout' ||
    key === NETWORK_STAKEOUT_DOCUMENT_TYPE ||
    key === 'vytyceni' ||
    subjectKey === 'stakeout'
  ) {
    return 'excavationPermits';
  }
  if (key === 'vyjadreni_site' || agendaKind === 'network_crossing') {
    return 'statementsIs';
  }

  const discovered = discoveryEntry(subjectKey || key);
  if (discovered?.category === 'krizeni_siti') {
    return 'statementsIs';
  }
  if (discovered?.category === 'dotcene_organy') {
    return 'doss';
  }
  if (agendaKind === 'official' || key === 'vyjadreni_doss' || key === 'stanovisko_hzs') {
    return 'doss';
  }

  if (documentType || subjectKey || agendaKind) {
    const category = uradyCategoryForObligation({
      agendaKind: agendaKind || null,
      subjectKey: subjectKey || null,
      documentType: documentType || null,
    });
    if (category === 'krizeni_siti') return 'statementsIs';
    if (category === 'dotcene_organy' && (documentType || subjectKey || agendaKind === 'official')) {
      return 'doss';
    }
  }

  if (
    isAuthorityPovinnostKey(key) ||
    isAuthorityPovinnostLegacyKey(key) ||
    agendaKind === 'construction_prep'
  ) {
    // Prep checklist without a dedicated leaf stays in Pošta archive.
    return PROJECT_DRIVE_POSTA_KEY;
  }

  return PROJECT_DRIVE_POSTA_KEY;
}

/** Validate / normalize an artifact folder key for communication uploads. */
export function authorityCommunicationDriveArtifactFolderKey(
  raw: string | null | undefined,
): ProjectDriveArtifactFolderKey {
  const key = (raw ?? '').trim() as ProjectDriveArtifactFolderKey;
  if (key && projectDriveSkeletonEntry(key)) return key;
  return PROJECT_DRIVE_POSTA_KEY;
}

export function authorityCommunicationDocumentDescription(direction: string | null | undefined): string {
  return (direction ?? '').trim().toLowerCase() === 'inbound'
    ? 'Dokument komunikace — odpověď'
    : 'Kopie odchozí přílohy komunikace';
}
